<template>
  <b-card>
    <b-card-title>
      <b-row>
        <b-col cols="10">
          <h4 class="card-title">
            Filters
          </h4>
        </b-col>
        <b-col cols="2">
          <b-link
            v-if="pageFilters.checkbox.length || getFilterMultipleSelectOptions().length || getFilterSearchAutosuggestOptions().length"
            @click="clear()"
          >
            <feather-icon
              size="20"
              icon="XCircleIcon"
            />
          </b-link>
        </b-col>
      </b-row>
    </b-card-title>
    <b-row>
      <b-col>
        <b-button
          v-for="(item, index) in pageFilters.checkbox"
          :key="`display_filter_checkbox_${index}`"
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="outline-primary"
          size="sm"
          class="mr-1 mb-1"
          @click="removeFilterCheckbox(item.v)"
        >
          <span class="align-middle">{{ getFilterCheckboxLabel(item) }}</span>
          <feather-icon
            icon="XIcon"
            class="ml-25"
          />
        </b-button>
        <b-button
          v-for="(item, index) in getFilterMultipleSelectOptions()"
          :key="`display_filter_multipleSelect_${index}`"
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="outline-primary"
          size="sm"
          class="mr-1 mb-1"
          @click="removeFilterMultipleSelect(item.value)"
        >
          <span class="align-middle">{{ item.label }}</span>
          <feather-icon
            icon="XIcon"
            class="ml-25"
          />
        </b-button>
        <b-button
          v-for="(item, index) in getFilterSearchAutosuggestOptions()"
          :key="`display_filter_searchAutosuggest_${index}`"
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="outline-primary"
          size="sm"
          class="mr-1 mb-1"
          @click="removeSearchAutosuggest(item.value)"
        >
          <span class="align-middle">{{ item.label }}</span>
          <feather-icon
            icon="XIcon"
            class="ml-25"
          />
        </b-button>
      </b-col>
    </b-row>
    <app-collapse>
      <app-collapse-item
        v-for="(item, index) in filters"
        :key="`filter_${item.key}`"
        :title="item.title"
      >
        <b-form-checkbox-group
          v-if="item.filter_type === 'checkbox'"
          v-model="pageFilters.checkbox"
          :options="item.data"
          value-field="v"
          text-field="l"
          class="page-filter-checkbox-group"
        />
        <v-select
          v-if="item.filter_type === 'multiple-select'"
          v-model="pageFilters.multipleSelect[item.key]"
          multiple
          :options="item.data"
          label="l"
          value-field="v"
          class="page-filter-multiple-select"
        />
        <vue-autosuggest
          v-if="item.filter_type === 'search_autosuggest'"
          :id="`autosuggest-${item.key}`"
          v-model="pageFilters.searchAutosuggest[item.key]"
          :suggestions="searchAutosuggestFilteredOptions"
          :limit="autosuggestLimit"
          :input-props="{id:`autosuggest__input-${item.key}`, class:'form-control', placeholder:'Search Keywords'}"
          @focus="searchAutosuggestFocusMe"
          @selected="onSearchAutosuggestSelected"
        >
          <template slot-scope="{suggestion}">
            <span class="my-suggestion-item">{{ suggestion.item }}</span>
          </template>
        </vue-autosuggest>
      </app-collapse-item>
    </app-collapse>
    <b-row class="mt-1">
      <b-col>
        <b-button
          variant="gradient-primary"
          block
          @click="filter()"
        >
          Filter
        </b-button>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import {
    BFormCheckboxGroup,
    BCard,
    BCardTitle,
    BRow,
    BCol,
    BButton,
    BLink,
} from 'bootstrap-vue'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import Ripple from 'vue-ripple-directive'

import vSelect from 'vue-select'
import { VueAutosuggest } from 'vue-autosuggest'
import { coreApiPageFilterObject } from '@/coreApi/filters/coreApiPageFilter'

// eslint-disable-next-line no-unused-vars
const querystring = require('querystring')

export default {
    components: {
        BCard,
        BCardTitle,
        AppCollapse,
        AppCollapseItem,
        BFormCheckboxGroup,
        BRow,
        BCol,
        BButton,
        vSelect,
        BLink,
        VueAutosuggest,
    },
    directives: {
        Ripple,
    },
    props: {
        filters: {
            type: Array,
            default: () => coreApiPageFilterObject.getFilterList(),
        },
    },
    data() {
        const searchAutosuggestsAll = {}
        this.filters.forEach(item => {
            if (item.filter_type === 'search_autosuggest') {
                searchAutosuggestsAll[item.key] = item.data
            }
        })
        return {
            pageFilters: this.defaultPageFilters(),
            autosuggestLimit: 10,
            searchAutosuggestsAll,
            searchAutosuggestFocused: '',
        }
    },
    computed: {
        searchAutosuggestFilteredOptions() {
            return [
                {
                    data: this.searchAutosuggestsAll[this.searchAutosuggestFocused] ? this.searchAutosuggestsAll[this.searchAutosuggestFocused].filter(option => option.toLowerCase().indexOf(this.pageFilters.searchAutosuggest[this.searchAutosuggestFocused].toLowerCase()) > -1) : [],
                },
            ]
        },
    },
    watch: {
        $route() {
            this.initFilters()
        },
    },
    created() {
        this.initFilters()
    },
    methods: {
        initFilters() {
            const pageFilters = coreApiPageFilterObject.getQueryStringFilter()
            if (pageFilters) {
                this.pageFilters = pageFilters
            } else {
                this.pageFilters = this.defaultPageFilters()
            }
        },
        defaultPageFilters() {
            const pageFilters = {
                checkbox: [],
                multipleSelect: {},
                searchAutosuggest: {},
            }
            this.filters.forEach(item => {
                if (item.filter_type === 'multiple-select') {
                    pageFilters.multipleSelect[item.key] = []
                }
                if (item.filter_type === 'search_autosuggest') {
                    pageFilters.searchAutosuggest[item.key] = ''
                }
            })
            return pageFilters
        },
        filter() {
            const newPath = coreApiPageFilterObject.getFilterUrl(this.pageFilters)
            if (newPath !== this.$route.fullPath) {
                this.$router.push(newPath)
            }
        },
        clear() {
            this.pageFilters = this.defaultPageFilters()
            this.filter()
        },
        getFilterCheckboxLabel(value) {
            let label = ''
            // eslint-disable-next-line no-restricted-syntax
            for (const [, filter] of Object.entries(this.filters)) {
                // eslint-disable-next-line no-restricted-syntax
                for (const [, filterData] of Object.entries(filter.data)) {
                    if (filterData.v === value) {
                        label = filterData.l
                    }
                }
            }
            return label
        },
        getFilterMultipleSelectOptions() {
            const multipleSelectOptions = []
            // eslint-disable-next-line no-restricted-syntax
            for (const [, options] of Object.entries(this.pageFilters.multipleSelect)) {
                // eslint-disable-next-line no-restricted-syntax
                for (const option of options) {
                    multipleSelectOptions.push({
                        value: option.v,
                        label: option.l,
                    })
                }
            }
            return multipleSelectOptions
        },
        getFilterSearchAutosuggestOptions() {
            const searchAutosuggestOptions = []
            // eslint-disable-next-line no-restricted-syntax
            for (const [filterKey, filterValue] of Object.entries(this.pageFilters.searchAutosuggest)) {
                // eslint-disable-next-line no-restricted-syntax
                if (filterValue) {
                    let filterLabel = filterValue
                    if (filterKey === 'kwn') {
                        filterLabel = `~${filterValue}`
                    }
                    searchAutosuggestOptions.push({
                        value: `${filterKey}:${filterValue}`,
                        label: filterLabel,
                    })
                }
            }
            return searchAutosuggestOptions
        },
        removeFilterCheckbox(value) {
            // eslint-disable-next-line no-restricted-syntax
            this.pageFilters.checkbox.forEach((filterData, index) => {
                if (filterData.v === value) {
                    this.$delete(this.pageFilters.checkbox, index)
                }
            })
        },
        removeFilterMultipleSelect(value) {
            // eslint-disable-next-line no-restricted-syntax
            for (const [, options] of Object.entries(this.pageFilters.multipleSelect)) {
                options.forEach((filterData, index) => {
                    if (filterData.v === value) {
                        this.$delete(options, index)
                    }
                })
            }
        },
        removeSearchAutosuggest(valueObject) {
            const valueObjectSplit = valueObject.split(':')
            const key = valueObjectSplit[0]
            const value = valueObjectSplit[1]
            // eslint-disable-next-line no-restricted-syntax
            for (const [filterValueKey, filterValue] of Object.entries(this.pageFilters.searchAutosuggest)) {
                if (key === filterValueKey && filterValue === value) {
                    this.pageFilters.searchAutosuggest[filterValueKey] = ''
                }
            }
        },
        searchAutosuggestFocusMe(e) {
            if (e.target && e.target.id) {
                const re = /.*-(.*)$/
                const autosuggestKey = re.exec(e.target.id, /.*-(.*)$/)
                this.searchAutosuggestFocused = autosuggestKey[1] || ''
            }
        },
        onSearchAutosuggestSelected(item) {
            this.pageFilters.searchAutosuggest[this.searchAutosuggestFocused] = item.item
        },
    },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
<style lang="scss">
@import '@core/scss/vue/libs/vue-autosuggest.scss';
</style>
